import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { LocaleSwitcher } from '../components/preferences/LocaleSwitcher';
import { ThemeToggle } from '../components/preferences/ThemeToggle';
import { cn } from '../utils/cn';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n/I18nContext';

type NavItem = {
  labelKey: string;
  to: string;
  sectionKey: string;
  icon: React.ReactNode;
  access?: string[];
  /** Reserved for the platform project; `access` flags cannot unlock it. */
  platformOnly?: boolean;
};

const icon = (d: string) => (
  <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const NAV: NavItem[] = [
  { labelKey: 'nav.items.dashboard', to: '/app/dashboard', sectionKey: 'nav.sections.overview', access: ['dashboard'],
    icon: icon('M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z') },
  { labelKey: 'nav.items.bookings', to: '/app/booking/bookings', sectionKey: 'nav.sections.booking', access: ['booking'],
    icon: icon('M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5') },
  { labelKey: 'nav.items.services', to: '/app/booking/services', sectionKey: 'nav.sections.booking', access: ['booking'],
    icon: icon('M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085') },
  { labelKey: 'nav.items.serviceCategories', to: '/app/booking/categories', sectionKey: 'nav.sections.booking', access: ['booking'],
    icon: icon('M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z M6 6h.008v.008H6V6Z') },
  { labelKey: 'nav.items.customForms', to: '/app/booking/custom-forms', sectionKey: 'nav.sections.booking', access: ['booking'],
    icon: icon('M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z') },
  { labelKey: 'nav.items.users', to: '/app/maintenance/users', sectionKey: 'nav.sections.maintenance', access: ['user'],
    icon: icon('M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z') },
  { labelKey: 'nav.items.customers', to: '/app/maintenance/customers', sectionKey: 'nav.sections.maintenance', access: ['customer'],
    icon: icon('M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z') },
  { labelKey: 'nav.items.setting', to: '/app/setting', sectionKey: 'nav.sections.configuration',
    icon: icon('M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z') },
  { labelKey: 'nav.items.appearance', to: '/app/appearance', sectionKey: 'nav.sections.configuration',
    icon: icon('M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z') },
  { labelKey: 'nav.items.aboutUs', to: '/app/about-us', sectionKey: 'nav.sections.configuration',
    icon: icon('m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z') },
  { labelKey: 'nav.items.whatsapp', to: '/app/whatsapp', sectionKey: 'nav.sections.configuration',
    icon: icon('M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z') },
  { labelKey: 'nav.items.projects', to: '/app/platform/projects', sectionKey: 'nav.sections.platform', platformOnly: true,
    icon: icon('M2.25 21h19.5M3.75 3v18m4.5-18v18m4.5-18v18m4.5-18v18M6 6.75h.008v.008H6V6.75Zm0 3.75h.008v.008H6v-.008Zm0 3.75h.008v.008H6v-.008Zm4.5-7.5h.008v.008h-.008V6.75Zm0 3.75h.008v.008h-.008v-.008Zm0 3.75h.008v.008h-.008v-.008Zm4.5-7.5h.008v.008h-.008V6.75Zm0 3.75h.008v.008h-.008v-.008Zm0 3.75h.008v.008h-.008v-.008Z') },
];

function BreadcrumbTitle() {
  const location = useLocation();
  const { t } = useI18n();
  const title = useMemo(() => {
    const hit = NAV.find((n) => location.pathname.startsWith(n.to));
    return hit ? t(hit.labelKey) : t('common.admin');
  }, [location.pathname, t]);
  return (
    <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</div>
  );
}

function Sidebar({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { user, isPlatform } = useAuth();
  const { t } = useI18n();
  const roleAccess = user?.roleAccess;
  const isBatamx = user?.role === 'batamx';

  const grouped = useMemo(() => {
    const visible = NAV.filter((item) => {
      if (item.platformOnly) return isPlatform;
      if (!item.access) return true;
      if (isBatamx) return true;
      return item.access.some((key) => roleAccess?.[key]);
    });
    const map = new Map<string, NavItem[]>();
    for (const item of visible) {
      const key = item.sectionKey;
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return Array.from(map.entries());
  }, [roleAccess, isBatamx, isPlatform]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm transition md:hidden',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
      />
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[min(80vw,280px)] overflow-y-auto overscroll-contain border-r border-slate-200 bg-white p-4 transition md:sticky md:top-0 md:h-screen md:w-[280px] md:translate-x-0 dark:border-slate-800 dark:bg-slate-950',
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
        aria-label={t('common.aria.sidebarNavigation')}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="BatamX" className="h-9 w-9 rounded-xl object-cover" />
            <div>
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('common.appName')}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{t('common.platformConsole')}</div>
            </div>
          </div>
          <button
            className="md:hidden rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            onClick={onClose}
            aria-label={t('common.aria.closeSidebar')}
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="mt-6 space-y-6">
          {grouped.map(([sectionKey, items]) => (
            <div key={sectionKey}>
              <div className="mb-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
                {t(sectionKey)}
              </div>
              <nav className="space-y-1">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition md:py-2',
                        isActive
                          ? 'bg-red-50 text-red-700 font-medium dark:bg-red-950/50 dark:text-red-300'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900'
                      )
                    }
                    onClick={onClose}
                    end
                  >
                    {item.icon}
                    {t(item.labelKey)}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  /* Stop the page behind the mobile drawer from scrolling while it is open. */
  useEffect(() => {
    if (!sidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 md:flex dark:bg-slate-950 dark:text-slate-100">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:px-4 md:px-6">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <button
                className="-ml-1 shrink-0 rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden dark:text-slate-300 dark:hover:bg-slate-800"
                onClick={() => setSidebarOpen(true)}
                aria-label={t('common.aria.openSidebar')}
                type="button"
              >
                ☰
              </button>
              <BreadcrumbTitle />
            </div>

            <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
              <LocaleSwitcher />
              <ThemeToggle />
              <div className="hidden sm:block text-right">
                <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  {user?.name ?? t('common.notAvailable')}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">{user?.role}</div>
              </div>
              <div className="hidden h-9 w-9 place-items-center rounded-full bg-slate-900 text-xs font-semibold text-white sm:grid dark:bg-slate-100 dark:text-slate-900">
                {(user?.name?.[0] ?? 'A').toUpperCase()}
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="px-2 sm:px-3"
                aria-label={t('common.logout')}
                title={t('common.logout')}
                onClick={() => {
                  logout();
                  navigate('/login', { replace: true });
                }}
              >
                {/* Icon-only below `sm` so the header fits a 360px viewport. */}
                <svg className="h-4 w-4 sm:hidden" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                </svg>
                <span className="hidden sm:inline">{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-5 sm:px-4 sm:py-6 md:px-6">
          <Outlet />
        </main>

        <footer className="border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-4 text-xs text-slate-500 sm:px-4 md:px-6 dark:text-slate-400">
            <div>{t('common.footer.copyright', { year: new Date().getFullYear() })}</div>
            <div className="hidden sm:block">
              {t('common.footer.tagline')}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
